import { Alert, Group, Text, Badge, Button } from "@mantine/core";
import {
  IconShieldCheck,
  IconAlertCircle,
  IconClock,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { routePaths } from "../lib/routePaths";

interface AuditBannerProps {
  type: "pending-request" | "active-audit";
  data: {
    organization?: string;
    audit_type?: string;
    auditor_organization?: string;
    days_remaining?: number;
    progress_percentage?: number;
    request_count?: number;
    active_count?: number;
  };
}

/**
 * Banner component to display audit alerts on the dashboard
 * Shows pending audit requests or active audits
 *
 * Genuinely shared (frontend split epic Phase 7, Task 7.1 real-caller
 * check): apps/portal's own dashboard widget registration
 * (features/audit-management/dashboard/AuditBannersCard.tsx) renders this
 * directly, alongside apps/auditor's own separate need for it — not
 * auditor-exclusive despite living in a directory named for audit.
 */
export function AuditBanner({ type, data }: AuditBannerProps) {
  if (type === "pending-request") {
    return (
      <Alert
        icon={<IconAlertCircle size={20} />}
        title="Pending Audit Request"
        color="yellow"
        variant="light"
      >
        <Group justify="space-between" wrap="nowrap">
          <div>
            <Text size="sm">
              {data.request_count === 1
                ? `You have ${data.request_count} pending audit request requiring review from ${data.organization}.`
                : `You have ${data.request_count} pending audit requests requiring review.`}
            </Text>
            {data.audit_type && (
              <Text size="xs" c="dimmed" mt="xs">
                Type: {data.audit_type.replace(/_/g, " ")}
              </Text>
            )}
          </div>
          <Button
            component={Link}
            // US-155 (F-115): name the tab explicitly rather than relying on it
            // being the default — the default is free to change.
            to={routePaths.auditManagement({ tab: "requests" })}
            variant="light"
            size="sm"
            color="yellow"
          >
            Review Requests
          </Button>
        </Group>
      </Alert>
    );
  }

  // Active audit banner
  return (
    <Alert
      icon={<IconShieldCheck size={20} />}
      title="Active Audit in Progress"
      color="blue"
      variant="light"
    >
      <Group justify="space-between" wrap="nowrap">
        <div>
          <Text size="sm">
            {data.active_count === 1
              ? `There is currently ${data.active_count} active audit at this facility by ${data.auditor_organization}.`
              : `There are currently ${data.active_count} active audits at this facility.`}
          </Text>
          <Group gap="xs" mt="xs">
            {data.days_remaining !== undefined && data.days_remaining >= 0 && (
              <Badge
                size="sm"
                variant="light"
                color={data.days_remaining < 3 ? "orange" : "blue"}
                leftSection={<IconClock size={12} />}
              >
                {data.days_remaining} day{data.days_remaining !== 1 ? "s" : ""}{" "}
                remaining
              </Badge>
            )}
            {data.progress_percentage !== undefined && (
              <Badge size="sm" variant="light" color="blue">
                {data.progress_percentage}% complete
              </Badge>
            )}
          </Group>
        </div>
        <Group gap="xs" wrap="nowrap">
          {/* US-155 (F-115): these two used to be the SAME href — three links
              on this banner all resolved to /audit-management and all landed on
              the default Audit Requests tab, so neither label was true. The tab
              now lives in the URL (see AuditManagementPage), which is what makes
              a distinct destination possible at all. */}
          <Button
            component={Link}
            to={routePaths.auditManagement({ tab: "findings" })}
            variant="subtle"
            size="sm"
            color="blue"
          >
            View Findings
          </Button>
          <Button
            component={Link}
            to={routePaths.auditManagement({ tab: "active" })}
            variant="light"
            size="sm"
            color="blue"
          >
            View Details
          </Button>
        </Group>
      </Group>
    </Alert>
  );
}
